// TextField.js
import { CSSResultGroup, LitElement, html } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import '../icon/icon.element';
import { textFieldStyles } from './text-field.styles';
import { styleMap } from 'lit/directives/style-map.js';

/**
 * Text field
 *
 * @prop {boolean} disabled - If text field is disabled.
 * @prop {boolean} password - If text field accepts passwords.
 * @prop {boolean} readonly - If text field is readonly.
 * @prop {boolean} required - If text field is required.
 * @prop {string} placeholder - Sets the placeholder for text field
 * @prop {string} value - Sets the Value for text field
 *
 * @fires change - Fires when the text field is edited.
 */
@customElement('cds-text-field')
export class TextFieldElement extends LitElement {
  static styles: CSSResultGroup = textFieldStyles;

  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) password = false;
  @property({ type: String }) placeholder = '';
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) required = false;
  @property({ type: String }) value = '';

  @query('input') private input!: HTMLInputElement;

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    this.classList.add('cds-text-field');
    if (this.value && !this.disabled) {
      this.classList.add('filled');
    } else {
      this.classList.remove('filled');
    }
  }

  render() {
    const styles = styleMap({
      visibility: this.clearable && !this.disabled ? 'visible' : 'hidden',
    });

    return this.readonly
      ? html`<span class="readonly-container">${this.readonlyValue}</span>`
      : html`
          <input
            class="cds-text-field--input"
            ?disabled="${this.disabled}"
            .placeholder="${this.placeholder}"
            ?required="${this.required}"
            type="${this.password ? 'password' : 'text'}"
            .value="${this.value}"
            @blur="${this.onBlur}"
            @focus="${this.onFocus}"
            @keydown="${this.onKeyDown}"
            @input="${this.onInputChange}"
          />
          <cds-icon
            class="cds-text-field--icon right"
            icon="close"
            size="s"
            style=${styles}
            @click="${this.clear}"
          ></cds-icon>
        `;
  }

  /** Handle input change */
  private onInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    this.value = input.value;
    this.dispatchEvent(new CustomEvent('change', { detail: this.value }));
  }

  /** Handle key down event (Escape to clear input) */
  private onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.clear();
    }
  }

  /** Handle focus event */
  private onFocus() {
    this.classList.add('focus');
    this.dispatchEvent(new CustomEvent('focused'));
  }

  /** Handle blur event */
  private onBlur() {
    this.classList.remove('focus');
    this.dispatchEvent(new CustomEvent('blurred'));
  }

  /** Clear input value */
  private clear() {
    if (!this.clearable) return;
    this.value = '';
    this.input.value = '';
    this.input.focus();
    this.dispatchEvent(new CustomEvent('change', { detail: this.value }));
  }

  /** Get readonly display value */
  private get readonlyValue() {
    return this.password ? '*'.repeat(this.value.length || 0) : this.value;
  }

  /** Determine if the input is clearable */
  private get clearable() {
    return !!this.value && !this.password;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cds-text-field': TextFieldElement;
  }
}
